import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useState } from 'react'

import { DurationForm } from './duration-form'
import { OrderSummary } from './order-summary'
import { PaymentError } from './payment-error'
import { VisitorDataForm } from './visitor-data-form'
import {
  VISITOR_PARKING_CURRENCY,
  VISITOR_PARKING_SCREENS,
  VisitorParkingScreens,
} from './visitor-parking.constants'
import * as S from './visitor-parking.page.styled'
import { useVisitorParkingStore } from './visitor-parking.store'
import { WelcomeScreen } from './welcome-screen'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

interface VisitorParkingRendererProps {
  screen: VisitorParkingScreens
  setScreen: (screen: VisitorParkingScreens) => void
}

const VisitorParkingRenderer = ({ screen, setScreen }: VisitorParkingRendererProps) => {
  const { paymentSession } = useVisitorParkingStore()

  switch (screen) {
    case VISITOR_PARKING_SCREENS.WELCOME:
      return <WelcomeScreen onNext={() => setScreen(VISITOR_PARKING_SCREENS.VISITOR_DETAILS)} />
    case VISITOR_PARKING_SCREENS.VISITOR_DETAILS:
      return (
        <VisitorDataForm
          onBack={() => setScreen(VISITOR_PARKING_SCREENS.WELCOME)}
          onNext={() => setScreen(VISITOR_PARKING_SCREENS.DURATION)}
        />
      )
    case VISITOR_PARKING_SCREENS.DURATION:
      return (
        <DurationForm
          onBack={() => setScreen(VISITOR_PARKING_SCREENS.VISITOR_DETAILS)}
          onNext={() => setScreen(VISITOR_PARKING_SCREENS.SUMMARY)}
        />
      )
    case VISITOR_PARKING_SCREENS.SUMMARY:
      if (!paymentSession) return null

      return (
        <Elements
          options={{
            amount: paymentSession.amount,
            currency: VISITOR_PARKING_CURRENCY,
            mode: 'payment',
          }}
          stripe={stripePromise}
        >
          <OrderSummary
            onBack={() => setScreen(VISITOR_PARKING_SCREENS.DURATION)}
            onPaymentError={() => setScreen(VISITOR_PARKING_SCREENS.PAYMENT_ERROR)}
          />
        </Elements>
      )
    case VISITOR_PARKING_SCREENS.PAYMENT_ERROR:
      return <PaymentError onRetryPayment={() => setScreen(VISITOR_PARKING_SCREENS.SUMMARY)} />
    default:
      return null
  }
}

const VisitorParking = () => {
  const [screen, setScreen] = useState<VisitorParkingScreens>(VISITOR_PARKING_SCREENS.WELCOME)

  return (
    <S.Container>
      <VisitorParkingRenderer screen={screen} setScreen={setScreen} />
    </S.Container>
  )
}

export { VisitorParking }
